import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function PaintProducts() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-20 pb-20">
        {/* Hero */}
        <div className="relative h-[400px] md:h-[500px] mb-32 overflow-hidden rounded-[12px] mx-6 md:mx-12">
          <img
            src="https://pim-b2c.maxbo.no/_default_upload_bucket/maxbo/Innhold/Artikler/image-thumb__52833__b2c-image-brick/Utemaling_milj%C3%B8_2_1600x900.png"
            alt="DRYGOLIN Nordic Extreme Supermatt"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white mb-6">Kvalitet</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] text-white mb-6">
                DRYGOLIN <span className="italic">Nordic Extreme Supermatt</span>
              </h1>
              <p className="font-body text-white/80 text-lg max-w-2xl leading-relaxed">
                Premium hybridmaling fra Jotun — valgt for ekstrem holdbarhet og skittavvisende egenskaper.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Product Showcase */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-32">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <img
                  src="https://www.jotun.com/contentassetsjot03/aac928d001f8418f9491ca3efc8eb71b/drygolin-produkter-2.jpg?width=800&height=600&quality=85"
                  alt="DRYGOLIN Nordic Extreme Supermatt produkter"
                  className="w-full rounded-[12px] shadow-lg"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Produkt</p>
                <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">DRYGOLIN Nordic Extreme <span className="italic">Supermatt</span></h2>
                <p className="font-body text-foreground/70 text-base leading-relaxed mb-8 max-w-3xl">
                  DRYGOLIN Nordic Extreme Supermatt er en premium hybridmaling fra Jotun designet for nordiske klimaer. Den kombinerer oljebasert grunnlak med akrylbasert topplakk for maksimal holdbarhet, motstand mot slitasje og en elegant matt finish.
                </p>
              </motion.div>
            </div>
          </motion.section>

          {/* Product Info */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-32">

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="bg-white rounded-[8px] border border-border/20 p-8">
                <h3 className="font-heading text-xl font-light mb-6 text-[#2a1810]">Egenskaper</h3>
                <ul className="space-y-4">
                  {[
                    'Premium hybridmaling – olje + akryl',
                    'Ekstrem motstand mot slitasje',
                    'Skittavvisende overflate',
                    'Matt, elegant finish',
                    'UV-bestandig – fargen holder seg stabil',
                    'Vedlikeholdsintervall: 15–20 år',
                    'Rask tørketid – 2 strøk på én dag',
                    'Beskyttelse mot svertesopp og alger'
                  ].map((feature, idx) => (
                    <li key={idx} className="font-body text-base text-foreground/70 flex items-start gap-3">
                      <span className="text-[#2d5a3d] flex-shrink-0 mt-1">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="bg-[#2d5a3d]/5 border-2 border-[#2d5a3d]/30 rounded-[8px] p-8">
                <h3 className="font-heading text-xl font-light mb-4 text-[#2a1810]">Hvorfor vi velger DRYGOLIN</h3>
                <p className="font-body text-base text-foreground/70 leading-relaxed mb-6">
                  DRYGOLIN Nordic Extreme Supermatt er utviklet spesifikt for nordisk klima – med ekstrem holdbarhet, motstand mot fukt, frost og UV-stråling. Den skittavvisende overflaten betyr mindre vedlikehold, og den elegante matte finish passer perfekt til moderne badstuarkitektur.
                </p>
                <p className="font-body text-base text-foreground/70 leading-relaxed">
                  Som sertifisert Jotun-leverandør sikrer vi autentisk produkt og profesjonell påføring for garantert resultat.
                </p>
              </motion.div>
            </div>
          </motion.section>

          {/* Product Gallery */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mb-32">
            <h2 className="font-heading text-3xl font-light leading-[1.1] mb-12 text-[#2a1810]">DRYGOLIN i praksis og <span className="italic">inspirasjon</span></h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { url: 'https://www.jotun.com/contentassetsjot03/5b360d592d0d41b19ab421caf52bef39/jotun_0025_roerosroed_2142_vestlandsroed_img_56a4334_v3-1.jpg?width=600&height=450&quality=85', alt: 'DRYGOLIN på hus' },
                { url: 'https://www.jotun.com/contentassetsjot03/8003a5c8fca54ba3b7f20cb75783b599/trebitt_90037_funkissort_trebitt_terrasse_9074_nordisk-tre_img_56a2164_v3-1.jpg?width=600&height=450&quality=85', alt: 'DRYGOLIN terrasse' },
                { url: 'https://www.jotun.com/contentassetsjot03/aac928d001f8418f9491ca3efc8eb71b/drygolin-produkter-2.jpg?width=600&height=450&quality=85', alt: 'DRYGOLIN produkter' },
                { url: 'https://www.jotun.com/contentassetsjot03/e3d92e88fa82e1df5ec0bcb39da60e96/drygolin-nordic-extreme-supermatt-fargekart.jpg?width=600&height=450&quality=85', alt: 'DRYGOLIN fargepallett' }
              ].map((img, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  className="rounded-[8px] overflow-hidden border border-border/20 hover:border-[#2d5a3d] transition-all"
                >
                  <img src={img.url} alt={img.alt} className="w-full h-64 object-cover" />
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Product Variants */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-32">
            <h2 className="font-heading text-3xl font-light leading-[1.1] mb-8 text-[#2a1810]">Tilgjengelige <span className="italic">Varianter</span></h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: 'Mørk brunn', code: '#6B4423' },
                { name: 'Sort', code: '#1A1A1A' },
                { name: 'Lys grå', code: '#C8C8C8' },
                { name: 'Mørk blå', code: '#1a3a52' },
                { name: 'Hvitt', code: '#FFFFFF' },
                { name: 'RAL Custom', code: 'Egendefinert' }
              ].map((variant, idx) => (
                <motion.div key={idx} whileHover={{ y: -4 }} className="bg-white rounded-[8px] border border-border/20 p-6 text-center hover:border-[#2d5a3d] transition-colors">
                  <div className="w-full h-24 rounded-[6px] mb-4 border-2 border-border/30" style={{ backgroundColor: variant.code === 'Egendefinert' ? '#E5E5E5' : variant.code }} />
                  <p className="font-heading text-sm font-light text-[#2a1810]">{variant.name}</p>
                  <p className="font-body text-base text-foreground/50 mt-1">{variant.code}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Jotun Link */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="bg-gradient-to-br from-[#2d5a3d]/10 to-[#4a8f63]/5 border-2 border-[#2d5a3d]/20 rounded-[8px] p-12 mb-32">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl font-light mb-3 text-[#2a1810]">Utforsk Jotun sitt program</h3>
                <p className="font-body text-base text-foreground/70 max-w-2xl">
                  Se hele Jotun sitt sortiment av premium dekormaling og produkter designet for nordisk arkitektur.
                </p>
              </div>
              <motion.a 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }} 
                href="https://www.jotun.com/no-no/decorative/products/exterior" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-[#2d5a3d] text-white font-body text-sm font-semibold rounded-[4px] hover:bg-[#2d5a3d]/90 transition-all">
                Jotun.com <ExternalLink size={16} />
              </motion.a>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[6px] p-12 md:p-20 text-center">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste Steg</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6">
                Klar for<br /><span className="italic">Din Malte Badstue?</span>
              </h2>
              <p className="font-body text-lg text-white/70 mb-12 max-w-2xl mx-auto">
                Velg din farge og la oss hjelpe deg med profesjonell påføring av DRYGOLIN Nordic Extreme Supermatt.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/be-om-tilbud"
                  className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-block">
                  BE OM TILBUD
                </motion.a>
              </div>
            </motion.div>
          </motion.section>
        </div>
      </div>

      <Footer />
    </div>
  );
}